import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Table } from '@yaradigitallabs/ahua-react';
import { useMemo, useState } from 'react';
import { createBulkImportsHistoryColumns } from './BulkImportsHistoryColumns';
import type { BulkImportHistoryItem } from '../types/bulkImportsHistory.types';
import { SoftTagTableSkeleton } from '@/components/skeleton';

type BulkImportsHistoryTableProps = {
  rows: BulkImportHistoryItem[];
  loading: boolean;
  onDownloadReport: (row: BulkImportHistoryItem) => void;
};

export const BulkImportsHistoryTable = ({
  rows,
  loading,
  onDownloadReport,
}: BulkImportsHistoryTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const data = useMemo(() => rows, [rows]);
  const columns = useMemo(
    () => createBulkImportsHistoryColumns(onDownloadReport),
    [onDownloadReport]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  return loading ? (
    <SoftTagTableSkeleton />
  ) : (
    <Table css={{ width: '100%' }}>
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <Table.Row key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <Table.SortHead
                css={{ paddingTop: '4px', paddingBottom: '4px' }}
                direction={header.column.getIsSorted()}
                active={Boolean(header.column.getIsSorted())}
                key={header.id}
                onClick={header.column.getToggleSortingHandler()}
                className="pl-3 pr-2 border-t border-r border-l border-[#E5E7EB]"
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </Table.SortHead>
            ))}
          </Table.Row>
        ))}
      </thead>
      <tbody>
        {data.length === 0 ? (
          <Table.Row>
            <Table.Cell
              colSpan={columns.length}
              className="text-center py-10 border-r border-[#E5E7EB]"
            >
              No bulk import history found.
            </Table.Cell>
          </Table.Row>
        ) : (
          table.getRowModel().rows.map(row => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map(cell => (
                <Table.Cell
                  css={{ paddingTop: '8px', paddingBottom: '8px' }}
                  key={cell.id}
                  className="px-4 py-0"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))
        )}
      </tbody>
    </Table>
  );
};
